// Shared helper: count how many of the "important" fields a form payload has populated.
// Used by:
//   - UploadStep (the post-OCR toast)
//   - ReviewStep (the header completeness badge — chips are clickable, jumping to the
//     relevant wizard step via the `step` index returned in each missing entry. Chips
//     also colour-code by required vs optional so users can tell at a glance which
//     gaps actually matter to the broker.)
// Single source of truth so the two views never drift.

// Step indexes mirror STEPS in FormWizard.js (keep in sync if step order changes):
//   0 dealer · 1 vehicle · 2 finance-type · 3 application-type · 4 licence
//   5 marital · 6 passport · 7 nationality · 8 dob · 9 personal-contact
//   10 address · 11 employment-status · 12 employment-details · 13 income
//   14 bank · 15 notes · 16 review
const STEP = {
  vehicle: 1,
  marital: 5,
  nationality: 7,
  dob: 8,
  personalContact: 9,
  address: 10,
  employmentDetails: 12,
  income: 13,
  bank: 14,
};

// Default populated check — treats undefined/null/empty-string/0 as missing.
// This is correct for free-text and date fields, and for numeric fields that
// the user must actively engage with (e.g. "years with bank" — 0 means
// "the user didn't select anything").
const defaultPopulated = (v) => v !== undefined && v !== null && v !== '' && v !== 0;

// Allows zero as a valid value. Use for numeric fields where 0 is a legitimate
// answer (e.g. dependants — "I have no dependants" is a real, common answer).
const numericPopulated = (v) => v !== undefined && v !== null && v !== '';

const FIELDS = [
  { key: 'first_name',           label: 'First name',     step: STEP.personalContact, required: true },
  { key: 'surname',              label: 'Surname',        step: STEP.personalContact, required: true },
  { key: 'date_of_birth',        label: 'Date of birth',  step: STEP.dob,             required: true },
  { key: 'email_address',        label: 'Email',          step: STEP.personalContact, required: true },
  { key: 'mobile_number',        label: 'Mobile',         step: STEP.personalContact, required: true },
  { key: 'marital_status',       label: 'Marital status', step: STEP.marital,         required: true },
  { key: 'nationality',          label: 'Nationality',    step: STEP.nationality,     required: true },
  { key: 'dependants',           label: 'Dependants',     step: STEP.personalContact, required: true, populated: numericPopulated },
  { key: 'employment_status',    label: 'Employment status', step: 11,                required: true },
  { key: 'vehicle_registration', label: 'Vehicle reg',    step: STEP.vehicle,         required: true },
  { key: 'vehicle_make',         label: 'Vehicle make',   step: STEP.vehicle,         required: true },
  { key: 'cash_price',           label: 'Cash price',     step: STEP.vehicle,         required: true },
  { key: 'sort_code',            label: 'Sort code',      step: STEP.bank,            required: false },
  { key: 'account_number',       label: 'Account number', step: STEP.bank,            required: false },
  { key: 'bank_name',            label: 'Bank name',      step: STEP.bank,            required: false },
  { key: 'bank_years',           label: 'Years with bank',step: STEP.bank,            required: false },
  { key: 'gross_annual_income',  label: 'Annual income',  step: STEP.income,          required: true },
  { key: 'monthly_income',       label: 'Monthly income', step: STEP.income,          required: true },
  // Optional — nice-to-have, broker won't reject without it. Renders as amber pill
  // with "(if known)" hint so dealers don't waste time chasing the applicant.
  { key: 'monthly_mortgage_rent',label: 'Rent / mortgage',step: STEP.income,          required: false },
];

const isFieldPopulated = (field, value) =>
  (field.populated || defaultPopulated)(value);

// Vehicle field keys — used to filter out the vehicle group entirely in
// credit-line / no-vehicle mode (Artis assigns a dummy car server-side, so we
// don't want the pill to nag dealers about vehicle fields the broker won't use).
const VEHICLE_KEYS = new Set(['vehicle_registration', 'vehicle_make', 'cash_price']);

export const computeFieldCompleteness = (data, options = {}) => {
  const { applicationMode = 'full', noReg = false } = options;
  const isCreditLine = applicationMode === 'credit-line';
  if (!data || typeof data !== 'object') {
    return { got: 0, total: FIELDS.length + 3, missing: [] };
  }
  const activeFields = isCreditLine
    ? FIELDS.filter((f) => !VEHICLE_KEYS.has(f.key))
    : noReg
    ? FIELDS.filter((f) => f.key !== 'vehicle_registration' && f.key !== 'vehicle_make')
    : FIELDS;
  const filledFields = activeFields.filter((f) => isFieldPopulated(f, data[f.key]));
  const missingFields = activeFields
    .filter((f) => !isFieldPopulated(f, data[f.key]))
    .map((f) => ({ label: f.label, step: f.step, required: f.required }));

  // List-shaped sections count as 1 unit each, gated on a sensible "is filled" anchor
  const hasHomeAddress = Array.isArray(data.addresses)
    && data.addresses.length > 0
    && Boolean(data.addresses[0]?.postcode);
  const hasEmployment = Array.isArray(data.employment_history)
    && data.employment_history.length > 0
    && Boolean(data.employment_history[0]?.employer);
  // Residential Status sits inside addresses[0]. Counted as its own "important" item
  // because brokers reject without it AND it's a common gap after WhatsApp pastes.
  const hasResidentialStatus = Array.isArray(data.addresses)
    && data.addresses.length > 0
    && Boolean(data.addresses[0]?.residential_status);

  const sectionsFilled = (hasHomeAddress ? 1 : 0)
    + (hasEmployment ? 1 : 0)
    + (hasResidentialStatus ? 1 : 0);
  const missingSections = [];
  if (!hasHomeAddress) missingSections.unshift({ label: 'Home address', step: STEP.address, required: true });
  if (!hasEmployment) missingSections.unshift({ label: 'Employment', step: STEP.employmentDetails, required: true });
  if (!hasResidentialStatus) missingSections.push({ label: 'Residential status', step: STEP.address, required: true });

  return {
    got: filledFields.length + sectionsFilled,
    total: activeFields.length + 3,
    missing: [...missingSections, ...missingFields],
  };
};
